import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';

// Animated Counter Component
const CountUp = ({ end, duration = 2, suffix = '', isInView }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isInView || hasAnimated.current) return;
    hasAnimated.current = true;

    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      
      // Easing function for smooth deceleration
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      
      if (progress < 1) {
        countRef.current = requestAnimationFrame(animate);
      }
    };
    
    countRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (countRef.current) {
        cancelAnimationFrame(countRef.current);
      }
    };
  }, [isInView, end, duration]);

  return <>{count}{suffix}</>;
};

const testimonials = [
  {
    quote: "For more than 30 years, IPMI has opened the world of precious metals to me. Through its relationships, education, and leadership opportunities—including the honor of serving as IPMI's 43rd President—it shaped not only my career, but my vocation. IPMI didn't just connect me to an industry; it gave me a place to serve.",
    author: "Becky Berube",
    title: "President & Co-Founder",
    company: "United Catalyst Corporation",
    subtitle: "IPMI Member since 1993",
    image: "https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/4hgp8gnk_Beck%20Berube%20headshot.webp"
  },
  {
    quote: "As IPMI celebrates its 50th Anniversary, I extend my sincere congratulations on this remarkable milestone and reflect with deep appreciation on its enduring leadership within the global precious metals community. Over the course of my long-standing engagement with LBMA, LPPM, IPA, and IPMI, IPMI has stood as a cornerstone of trust, professional excellence, and international collaborations. Since becoming a member in 2006, the relationships, knowledge exchange, and shared commitment fostered through IPMI have profoundly shaped my career and strengthened our organisation's long-standing global contribution, as Tanaka continues its legacy following its 140th anniversary last year and now enters its 141st year.",
    author: "Hitoshi Kosai",
    title: "Exclusive Advisor",
    company: "TANAKA PRECIOUS METAL GROUP Co.,Ltd / TANAKA PRECIOUS METAL TECHNOLOGIES Co.,Ltd",
    subtitle: "Member since 2006",
    image: "https://customer-assets.emergentagent.com/job_milestone-fifty-1/artifacts/u30xmuoi_Hitoshi-Kosai%402x-100.jpg"
  },
  {
    quote: "Being a member of IPMI has helped BASIQ to make invaluable connections in the industry, build a reputation, and grow our business. In parallel, many relationships developed and turned into friendships.",
    author: "Regine Albrecht",
    title: "President",
    company: "BASIQ Corporation",
    image: "https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/2ce5kcgq_2016%20Regine%20headshot.jpg"
  },
  {
    quote: "I joined IPMI in 2012 when I signed up to give a technical talk on Fire Assay; little did I know that this decision would shape the course of my career. Over the years, IPMI has given me opportunities I never imagined, serving as President (2022-2023), and as a member of the Board of Directors and Executive Committee. Through this organization, I've grown both professionally and personally, learning immeasurably from industry leaders and peers. More than that, I've built lifelong friendships that I truly cherish. IPMI's commitment to excellence and collaboration has made it a cornerstone of our industry, and I'm proud to celebrate 50 years of innovation and community.",
    author: "Zachary J. Henneman, PhD",
    title: "Laboratory & Technical Director",
    company: "Ledoux & Co.",
    image: "https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/3mqpf1ni_Henneman%20Headshot.png"
  },
  {
    quote: "IPMI is at the forefront of the metals recycling industry, creating a unique forum for industry experts to learn new ideas and connect with peers. The relationships and insights have helped guide Sabin Metals Corp for the last 40+ years.",
    author: "Samuel Sabin",
    title: "Corporate Development",
    company: "Sabin Metals Corp",
    subtitle: "Member since 1980",
    image: "https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/ibfjldpt_Sam%20Sabin%20testimonial.jpg"
  },
  {
    quote: "IPMI has been a key contributor to my personal and professional development during my 25 years of membership. Through networking with individuals in the technical, commercial, and legal areas associated with precious metals, I was able to leverage the knowledge gained to great advantage in my workplace at BASF and Engelhard. This result was unique in my experience as a 40 year employee in the chemical / precious metals industries. It also was of great value during my second career as an educator at the undergraduate and graduate levels.",
    author: "Robert M. Ianniello, Ph.D.",
    title: "IPMI Member",
    subtitle: "25 Years of Membership",
    image: "https://customer-assets.emergentagent.com/job_milestone-fifty-1/artifacts/zjbai0am_Bob%20Ianniello.png"
  },
  {
    quote: "For me personally, IPMI has been one of the most reliable places to build long-term relationships in the precious metals markets. Over fifteen years of membership, Kitco has benefited from the connections, conversations, and collaboration that only a community like IPMI can bring together.",
    author: "John Dourekas",
    title: "Media and Institutional Sales Director",
    company: "Kitco",
    image: "https://customer-assets.emergentagent.com/job_390cde5b-422d-4c41-b8a2-9021ac6fe4b8/artifacts/k3b01zwj_John%20D%20headshot.jpeg"
  }
];

const LegacyGratitude = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 8000); // Change every 8 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="legacy-gratitude-section" ref={sectionRef}>
      <div className="section-container">
        {/* Join the Next Chapter Header - moved here */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="section-header centered"
        >
          <h2 className="section-title">Join the Next Chapter</h2>
          <p className="section-description-large join-description">
            As we celebrate 50 years, we're looking forward to the next 50. Whether you're a longtime member or exploring IPMI for the first time, we'd love to hear from you.
          </p>
        </motion.div>

        {/* Testimonial Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="testimonial-carousel"
        >
          <div className="testimonial-container">
            <Quote className="quote-icon" size={40} />
            
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="testimonial-content"
              >
                {/* Headshot */}
                {currentTestimonial.image && (
                  <div className="testimonial-headshot">
                    <img 
                      src={currentTestimonial.image} 
                      alt={currentTestimonial.author}
                    />
                  </div>
                )}
                <blockquote className="testimonial-quote">
                  "{currentTestimonial.quote}"
                </blockquote>
                <div className="testimonial-attribution">
                  <span className="testimonial-author">— {currentTestimonial.author}</span>
                  <span className="testimonial-title">
                    {currentTestimonial.title}
                    {currentTestimonial.company && ` - ${currentTestimonial.company}`}
                  </span>
                  {currentTestimonial.subtitle && (
                    <span className="testimonial-subtitle">{currentTestimonial.subtitle}</span>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <div className="carousel-nav">
              <button 
                onClick={goToPrevious} 
                className="carousel-arrow carousel-arrow-prev"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={24} />
              </button>
              <button 
                onClick={goToNext} 
                className="carousel-arrow carousel-arrow-next"
                aria-label="Next testimonial"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>

          {/* Dots Indicator */}
          <div className="carousel-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="legacy-stats-row"
        >
          <div className="legacy-stat">
            <span className="legacy-stat-number">
              <CountUp end={50} duration={2.5} isInView={isInView} />
            </span>
            <span className="legacy-stat-label">Years of Excellence</span>
          </div>
          <div className="legacy-stat-divider"></div>
          <div className="legacy-stat">
            <span className="legacy-stat-number">
              <CountUp end={500} duration={2.5} suffix="+" isInView={isInView} />
            </span>
            <span className="legacy-stat-label">Member Organizations</span>
          </div>
          <div className="legacy-stat-divider"></div>
          <div className="legacy-stat">
            <span className="legacy-stat-number">
              <CountUp end={30} duration={2} suffix="+" isInView={isInView} />
            </span>
            <span className="legacy-stat-label">Countries Represented</span>
          </div>
        </motion.div>

        {/* Thank you message */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="connecting-message"
        >
          <span className="trust-text">Connecting the precious metals community since 1976</span>
        </motion.p>
      </div>
    </section>
  );
};

export default LegacyGratitude;
